import { useState, useRef, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MessageCircle, Send, Image, Search, X, Loader2 } from 'lucide-react';
import { useDraftChat, type DraftChatSource, type DraftChatMessage } from '@/hooks/useDraftChat';
import { cn } from '@/lib/utils';

interface DraftChatLiveProps {
  /** MLB: a drafts.id. NFL: an nfl_leagues.id. Whatever `source.keyColumn` is keyed by. */
  draftId: string | null;
  teamName: string | null;
  /**
   * SAB-848: which chat table to read and write. Omitted, this is MLB's draft_chat exactly
   * as before, so no existing caller changes. NFL passes NFL_DRAFT_CHAT.
   */
  source?: DraftChatSource;
  /**
   * SAB-848: resolve the author's display name for a message.
   *
   * Defaults to the row's denormalised `team_name`, which is what MLB's draft_chat
   * carries, so MLB is unchanged. NFL has no such column by design -- it resolves the
   * name from nfl_teams at render time, so renaming a team relabels the whole transcript
   * instead of leaving a trail of stale names. Without this the NFL author line would
   * render blank, since msg.team_name is simply undefined on those rows.
   */
  resolveAuthorName?: (msg: DraftChatMessage) => string;
}

// Giphy API — uses the free-tier public beta key as fallback
const GIPHY_API_KEY = import.meta.env.VITE_GIPHY_API_KEY || '';
const GIPHY_SEARCH_URL = 'https://api.giphy.com/v1/gifs/search';
const GIPHY_TRENDING_URL = 'https://api.giphy.com/v1/gifs/trending';

interface GiphyGif {
  id: string;
  title: string;
  images: {
    fixed_width_small: { url: string; width: string; height: string };
    fixed_width: { url: string; width: string; height: string };
  };
}

/** Detects if a message is a GIF URL */
const isGifMessage = (msg: string) =>
  /^https?:\/\/.*\.giphy\.com\/.*\.(gif|webp)/i.test(msg.trim());

const DraftChatLive = ({ draftId, teamName, source, resolveAuthorName }: DraftChatLiveProps) => {
  const { messages, sendMessage, sending, newMessageFlag } = useDraftChat(draftId, teamName, source);
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const initialScrollDone = useRef(false);

  // GIF picker state
  const [gifOpen, setGifOpen] = useState(false);
  const [gifSearch, setGifSearch] = useState('');
  const [gifs, setGifs] = useState<GiphyGif[]>([]);
  const [gifLoading, setGifLoading] = useState(false);
  const gifDebounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Track whether user is near the bottom of the scroll container
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const threshold = 100;
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
  }, []);

  // Scroll to bottom helper — uses setTimeout for reliable mobile rendering
  const scrollToBottom = useCallback((delay = 150) => {
    const el = scrollRef.current;
    if (el) {
      setTimeout(() => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
      }, delay);
    }
  }, []);

  // On initial load or when becoming visible (tab switch), scroll to bottom
  useEffect(() => {
    if (messages.length === 0) return;
    if (!initialScrollDone.current) {
      initialScrollDone.current = true;
      scrollToBottom(200);
      return;
    }
    // Handle tab-switch: if scrollRef has height but scroll is at 0, we likely just became visible
    const el = scrollRef.current;
    if (el && el.clientHeight > 0 && el.scrollTop === 0 && el.scrollHeight > el.clientHeight) {
      scrollToBottom(200);
    }
  }, [messages.length, scrollToBottom]);

  // On new messages, scroll only if user was near the bottom
  useEffect(() => {
    if (newMessageFlag === 0) return;
    if (isNearBottomRef.current) {
      scrollToBottom(100);
    }
  }, [newMessageFlag, scrollToBottom]);

  const handleSend = () => {
    if (!input.trim()) return;
    sendMessage(input);
    setInput('');
    // User just sent a message, force scroll to bottom
    isNearBottomRef.current = true;
    scrollToBottom(100);
  };

  const handleSendGif = (gif: GiphyGif) => {
    const gifUrl = gif.images.fixed_width.url;
    sendMessage(gifUrl);
    setGifOpen(false);
    setGifSearch('');
    setGifs([]);
    isNearBottomRef.current = true;
    scrollToBottom(100);
  };

  // Fetch trending GIFs when picker opens
  useEffect(() => {
    if (!gifOpen) return;
    if (gifSearch.trim()) return; // Don't fetch trending if user is searching
    setGifLoading(true);
    fetch(`${GIPHY_TRENDING_URL}?api_key=${GIPHY_API_KEY}&limit=20&rating=pg-13`)
      .then(r => r.json())
      .then(data => setGifs(data.data || []))
      .catch(() => setGifs([]))
      .finally(() => setGifLoading(false));
  }, [gifOpen]);

  // Debounced GIF search
  useEffect(() => {
    if (!gifOpen) return;
    if (gifDebounceRef.current) clearTimeout(gifDebounceRef.current);
    if (!gifSearch.trim()) {
      // Reload trending
      setGifLoading(true);
      fetch(`${GIPHY_TRENDING_URL}?api_key=${GIPHY_API_KEY}&limit=20&rating=pg-13`)
        .then(r => r.json())
        .then(data => setGifs(data.data || []))
        .catch(() => setGifs([]))
        .finally(() => setGifLoading(false));
      return;
    }
    gifDebounceRef.current = setTimeout(() => {
      setGifLoading(true);
      fetch(`${GIPHY_SEARCH_URL}?api_key=${GIPHY_API_KEY}&q=${encodeURIComponent(gifSearch)}&limit=20&rating=pg-13`)
        .then(r => r.json())
        .then(data => setGifs(data.data || []))
        .catch(() => setGifs([]))
        .finally(() => setGifLoading(false));
    }, 400);
    return () => { if (gifDebounceRef.current) clearTimeout(gifDebounceRef.current); };
  }, [gifSearch, gifOpen]);

  const formatTime = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  return (
    <Card className="glass-card flex flex-col flex-1 min-h-0">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <MessageCircle className="w-4 h-4" />
          Draft Chat
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 flex flex-col flex-1 min-h-0">
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto min-h-[80px] px-3 py-2 space-y-1.5"
        >
          {messages.length === 0 ? (
            <p className="text-center py-6 text-muted-foreground text-xs">
              No messages yet. Say something!
            </p>
          ) : (
            messages.map((msg) => (
              <div key={msg.id} className="text-sm rounded-md px-2 py-1 bg-muted/20">
                <span className="font-semibold text-primary">
                  {resolveAuthorName ? resolveAuthorName(msg) : msg.team_name}
                </span>
                <span className="text-muted-foreground text-[10px] ml-1.5">{formatTime(msg.created_at)}</span>
                {isGifMessage(msg.message) ? (
                  <div className="mt-1">
                    <img
                      src={msg.message.trim()}
                      alt="GIF"
                      className="rounded max-w-[200px] max-h-[150px]"
                      loading="lazy"
                    />
                  </div>
                ) : (
                  <div className="text-foreground text-xs leading-snug break-words">{msg.message}</div>
                )}
              </div>
            ))
          )}
        </div>

        {/* GIF Picker */}
        {gifOpen && (
          <div className="border-t border-border bg-background/95 backdrop-blur-sm">
            <div className="flex items-center gap-1.5 px-2 pt-2">
              <Search className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
              <Input
                value={gifSearch}
                onChange={(e) => setGifSearch(e.target.value)}
                placeholder="Search GIFs..."
                className="h-7 text-xs"
                autoFocus
              />
              <Button
                size="sm"
                variant="ghost"
                className="h-7 w-7 p-0 shrink-0"
                onClick={() => { setGifOpen(false); setGifSearch(''); setGifs([]); }}
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
            <div className="h-[160px] overflow-y-auto px-2 py-2">
              {gifLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
                </div>
              ) : gifs.length === 0 ? (
                <p className="text-center text-muted-foreground text-xs py-8">
                  {gifSearch.trim() ? 'No GIFs found' : 'Loading...'}
                </p>
              ) : (
                <div className="grid grid-cols-3 gap-1.5">
                  {gifs.map((gif) => (
                    <button
                      key={gif.id}
                      onClick={() => handleSendGif(gif)}
                      className="rounded overflow-hidden hover:ring-2 ring-primary transition-all cursor-pointer"
                    >
                      <img
                        src={gif.images.fixed_width_small.url}
                        alt={gif.title}
                        className="w-full h-auto"
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="px-2 pb-1.5">
              <img
                src="https://media.giphy.com/attribution/powered-by-giphy-dark.png"
                alt="Powered by GIPHY"
                className="h-3 opacity-50"
              />
            </div>
          </div>
        )}

        {teamName && (
          <div className="flex items-center gap-1.5 p-2 border-t border-border">
            <Button
              size="sm"
              variant={gifOpen ? 'secondary' : 'ghost'}
              className={cn("h-8 w-8 p-0 shrink-0", gifOpen && "bg-primary/20")}
              onClick={() => setGifOpen(!gifOpen)}
            >
              <Image className="w-3.5 h-3.5" />
            </Button>
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value.slice(0, 200))}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Send a message..."
              className="h-8 text-xs"
              maxLength={200}
            />
            <Button
              size="sm"
              onClick={handleSend}
              disabled={!input.trim() || sending}
              className="h-8 w-8 p-0 bg-primary hover:bg-primary/80 shrink-0"
            >
              <Send className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DraftChatLive;
